import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

/**
 * Searchable single-select combobox built on shadcn Command + Popover.
 *
 * Props:
 * - value: string
 * - onChange: (value: string) => void
 * - options: Array<{ value: string, label: string, hint?: string }>
 * - placeholder, searchPlaceholder, emptyMessage: string
 * - disabled: boolean
 * - testid: string
 * - allowClear: boolean (default true) — show "All" / clear option as first row
 * - clearLabel: string (default = placeholder)
 *
 * Use cases: marca/tipologia/modello filters where the user wants to type
 * to narrow down a long list of options.
 */
export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Seleziona...',
  searchPlaceholder = 'Cerca...',
  emptyMessage = 'Nessun risultato',
  disabled,
  testid,
  className = '',
  size = 'default',
  allowClear = true,
  clearLabel,
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value);
  const finalClearLabel = clearLabel || placeholder;
  const sizeClasses = size === 'sm' ? 'h-9 px-2.5 pr-8 text-xs' : 'h-11 px-3 pr-9 text-sm';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testid}
          className={`w-full ${sizeClasses} rounded-lg border bg-white font-medium text-foreground text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)] focus:border-transparent uppercase tracking-wide disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
        >
          <span className={`truncate ${selected ? '' : 'text-muted-foreground'}`}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown className={`ml-2 flex-shrink-0 text-muted-foreground ${size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[--radix-popover-trigger-width] max-h-80"
        align="start"
        data-testid={testid ? `${testid}-popover` : undefined}
      >
        <Command shouldFilter>
          <CommandInput placeholder={searchPlaceholder} className="h-10" />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {allowClear && (
                <CommandItem
                  key="__clear__"
                  value=""
                  onSelect={() => {
                    onChange('');
                    setOpen(false);
                  }}
                  className="text-muted-foreground italic"
                  data-testid={testid ? `${testid}-option-clear` : undefined}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${!value ? 'opacity-100' : 'opacity-0'}`}
                  />
                  {finalClearLabel}
                </CommandItem>
              )}
              {options.map(opt => (
                <CommandItem
                  key={opt.value}
                  // value is what cmdk filters against — include hint to make it searchable too
                  value={`${opt.label} ${opt.hint || ''}`}
                  onSelect={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  data-testid={testid ? `${testid}-option-${opt.value}` : undefined}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${opt.value === value ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <span className="flex-1 truncate">{opt.label}</span>
                  {opt.hint && (
                    <span className="ml-2 text-xs text-muted-foreground">{opt.hint}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
